/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * PaymentSupportModal — "write to us about this payment", in the page.
 * ---------------------------------------------------------------------
 * A mailto: hands the reader to whatever mail client their machine happens to
 * have, and on a machine with none configured it does nothing at all. The one
 * person who most needs to reach us is the one whose payment just failed, so
 * that dead link is the worst place in the product to have one.
 *
 * So this opens THE CONTACT FORM THAT ALREADY EXISTS, in a modal, with the
 * subject and the payment's details already written out.
 *
 * NOTHING HERE IS SEEDED OR DUPLICATED. The form is read from the contact page
 * at request time, which means:
 *
 *   • the fields, their validation, the button and the success message are
 *     whatever the form builder says — reword the contact form and this modal
 *     rewords itself;
 *   • it posts to the same /public/form/<id>/submit/ endpoint the contact page
 *     posts to, so the submission is recorded like any other;
 *   • the notification goes wherever the BACKEND already sends contact-form
 *     mail (FORM_NOTIFY_EMAILS), because that recipient list is keyed off the
 *     slug of the page the FORM belongs to, not the page it was submitted
 *     from. No backend change, and no mailbox configured in the web app.
 *
 * PORTABLE. The page is named by envConfig.contactPageSlug, nothing here names
 * an application, and the fields are matched by what they ARE (an email field,
 * a textarea, a field labelled "Subject") rather than by the ids this one
 * deployment happens to use.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon, Skeleton } from '../../../components/feed/FeedParts';
import FormBuilderSection from '../../../components/dynamic-page/sections/FormBuilderSection';
import { usePublicPage } from '../../../hooks/usePublicPage';
import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';
import { retrieve, KEY_ACCESS_TOKEN } from '../../../utils/storage_helper';
import { fetchMe } from '../profile/profileApi';
import { supportSubject, supportBody } from './PaymentCard';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** The form section on the contact page, whichever section it happens to be. */
export function formSectionOf(page) {
  const sections = page?.sections || [];
  const section = sections.find(
    (s) => (s.section_type || s.type) === 'form_builder'
      && (s.section_config || s.config)?.form_id,
  );
  return section ? (section.section_config || section.config) : null;
}

/*
 * WHICH FIELD GETS WHICH ANSWER, BY CONVENTION.
 *
 * Field ids belong to a deployment, not to us: this form's are
 * field_contact_subject and friends, another's will not be. So each answer is
 * placed by what the field IS — and the subject rule is the backend's own
 * rule (page_builder_views._render_submission_email), which turns a field
 * labelled "Subject" into the email's subject line. Matching anything else
 * would write our subject into a field the email then ignores.
 *
 * A form missing one of these simply does not get that answer; the reader
 * types it. Nothing is ever locked — every prefilled box can be edited.
 */
export function prefillFor(fields, { subject, message, name, email }) {
  const list = (fields || []).filter((f) => f && f.id);
  const out = {};

  const subjectField = list.find((f) => (
    String(f.label || '').trim().toLowerCase() === 'subject'
    || String(f.id).toLowerCase().endsWith('_subject')
  ));
  if (subjectField && subject) out[subjectField.id] = subject;

  const messageField = list.find((f) => f.type === 'textarea');
  if (messageField && message) out[messageField.id] = message;

  const emailField = list.find((f) => f.type === 'email');
  if (emailField && email) out[emailField.id] = email;

  // The first short text field that is not the subject is conventionally the
  // name — the same assumption the notification email makes when it decides
  // who wrote in.
  const nameField = list.find((f) => (
    f.type === 'text' && (!subjectField || f.id !== subjectField.id)
  ));
  if (nameField && name) out[nameField.id] = name;

  return out;
}

/** Every field on the form, rows or no rows. */
function fieldsOf(cfg) {
  const flat = [...(cfg?.fields || [])];
  (cfg?.rows || []).forEach((row) => flat.push(...(row.fields || [])));
  (cfg?.stages || []).forEach((stage) => {
    flat.push(...(stage.fields || []));
    (stage.rows || []).forEach((row) => flat.push(...(row.fields || [])));
  });
  return flat;
}

/** Their name as the form would want it written. */
function nameOf(me) {
  const joined = [me?.first_name, me?.last_name].filter(Boolean).join(' ').trim();
  return joined || me?.full_name || me?.name || '';
}

/*
 * The wait, in the same shimmer the rest of the feed waits in — and shaped
 * like the form that is about to replace it, so the modal does not jump when
 * the real fields arrive.
 */
function FormSkeleton() {
  return (
    <div className="fd-paysup-skel" aria-busy="true" aria-label="Loading the form">
      <Skeleton height={13} radius={6} style={{ width: '28%' }} />
      <Skeleton height={44} radius={10} />
      <Skeleton height={13} radius={6} style={{ width: '22%', marginTop: 6 }} />
      <Skeleton height={44} radius={10} />
      <Skeleton height={13} radius={6} style={{ width: '26%', marginTop: 6 }} />
      <Skeleton height={44} radius={10} />
      <Skeleton height={13} radius={6} style={{ width: '24%', marginTop: 6 }} />
      <Skeleton height={104} radius={10} />
      <Skeleton height={42} radius={10} style={{ width: 150, marginTop: 4 }} />
    </div>
  );
}

export default function PaymentSupportModal({ row, onClose }) {
  const slug = envConfig.contactPageSlug;
  const page = usePublicPage(slug);

  /*
   * Their name and address, so they are not asked for what we already know.
   * STRICTLY A CONVENIENCE: it is a separate read that is allowed to fail, and
   * a failure leaves two empty boxes rather than a broken modal. Only when
   * signed in, which on this page they always are.
   */
  const signedIn = !!retrieve(KEY_ACCESS_TOKEN);
  const meQ = useQuery({
    queryKey: ['me', 'payment-support'],
    queryFn: ({ signal }) => fetchMe(apiHelper, { signal }),
    enabled: signedIn,
    retry: false,
    staleTime: envConfig.staleTimeMs,
  });

  const cfg = useMemo(() => formSectionOf(page.data), [page.data]);

  const prefill = useMemo(() => prefillFor(fieldsOf(cfg), {
    subject: supportSubject(row),
    message: supportBody(row),
    name: nameOf(meQ.data),
    email: meQ.data?.email || '',
  }), [cfg, row, meQ.data]);

  // Escape closes it, as every other dismissible thing in the shell does.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * THE FORM IS NOT RENDERED UNTIL ITS PREFILL IS KNOWN.
   *
   * `prefill` seeds the first render only, so a name arriving a moment later
   * would never reach the boxes. The optional read therefore has to settle —
   * either way — before the form mounts. `isFetching` covers both, and a
   * failed read settles it just as well as a successful one.
   */
  const waiting = page.isLoading || (signedIn && meQ.isFetching);
  const failed = !waiting && (page.isError || !cfg);

  return (
    <div
      className="fd-paysup"
      role="dialog"
      aria-modal="true"
      aria-label={row ? `Write to us about order ${row.orderId}` : 'Write to us about a payment'}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="fd-paysup-card">
        <header className="fd-paysup-hd">
          <div>
            <b>Write to us about this payment</b>
            {row && <span className="fd-paysup-ord">Order {row.orderId}</span>}
          </div>
          {/* fd-iconbtn: the shell's own close button, so this one is the
              same shape, size and hover as the drawer's and the sidebar's. */}
          <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>

        <div className="fd-paysup-body">
          {waiting && <FormSkeleton />}

          {failed && (
            <div className="fd-paysup-err">
              <Icon name="error" />
              <b>We couldn&rsquo;t load the form</b>
              <p>
                Your payment details are safe — nothing was sent. Please try
                again, and if it keeps failing, reach us from the Contact Us
                page.
              </p>
              <button type="button" onClick={() => page.refetch()}>Try again</button>
            </div>
          )}

          {!waiting && !failed && (
            <FormBuilderSection
              config={cfg}
              pageVisibility={page.data?.visibility}
              prefill={prefill}
            />
          )}
        </div>
      </div>
    </div>
  );
}
